// Faz 8.6 / Batch A3 — the leave calendar Content component (ADR-014 §2),
// the monthly-grid port of NovemHR-ekranlar emp-leave-calendar.jsx (TcMonth).
//
// A SEPARATE surface from LeaveListContent: GET /v1/leave/requests renders as a
// month calendar instead of a queue. The server scopes rows per caller, and the
// self calendar adds the explicit, server-authoritative `mine=true` narrow so a
// manager's employee view never becomes a team calendar. ONE Content serves
// both /leave/calendar (admin hat) and /me/leave/calendar (employee hat).
//
// Each month asks the server for exactly that month (`from`/`to`, inclusive
// dates) and drains its pages. The old read followed the created_at keyset
// order with a "load more" button, so a month whose requests were created late
// rendered empty until the reader paged far enough — and nothing said so.
//
// Date discipline: start_date/end_date and holidays are CALENDAR DATES; "today"
// is the day in the viewer's effective time zone (lib/calendar-window.ts).
//
// Mockup elements with no backend are SKIPPED, not faked (port rule 8):
// member roles/colors, the team summary strip, the week view + export action,
// the department Gantt/heatmap (leave-conflict.jsx — deferred with the
// conflict-limits backend).

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, Link as RouterLink } from "react-router";
import { ChevronLeft, ChevronRight, Plus, Rows3 } from "lucide-react";

import {
  Alert,
  Badge,
  Button,
  Card,
  ErrorState,
  IconButton,
  Loading,
  PageHeader,
  ScopeGate,
} from "@novemai/components";

import { errorMessage } from "@/api/errors";
import { useTimeZone } from "@/api/use-time-zone";
import { CalendarGrid } from "@/components/calendar-grid";
import { PersonLink } from "@/components/person-link";
import { type LeaveRequestRow } from "@/features/leave/leave-api";
import {
  LEAVE_CAP_FILE,
  leaveCapabilities,
} from "@/features/leave/leave-capabilities";
import { LEAVE_WINDOW_CAP, useHolidays, useLeaveWindow } from "@/features/leave/leave-window-api";
import { useEffectivePermissions } from "@/features/roles-permissions/use-effective-permissions";
import {
  dateDayKeys,
  monthCells,
  monthRange,
  todayKey,
  type DayKey,
} from "@/lib/calendar-window";
import { formatDateRange } from "@/lib/date-range";

// Only the statuses that occupy calendar days (mockup: approved solid,
// pending dashed). Draft/rejected/cancelled rows don't block a day.
const VISIBLE_STATUSES = new Set(["approved", "submitted"]);

const shortId = (id: string): string => id.slice(0, 8);

// The calendar exists to answer "who is away when", so the chip must carry the
// person, not their key. GET /v1/leave/requests already ships the SEM-301
// employee_name join (the list view has consumed it since); the id slice stays
// only as the null-name fallback the join documents (unresolved/redacted).
const displayName = (row: LeaveRequestRow): string =>
  row.employee_name ?? shortId(row.employee_id);

export function LeaveCalendarContent(): React.ReactElement {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const { capabilities: allowedCells } = useEffectivePermissions();
  const capabilities = leaveCapabilities(allowedCells);
  const isSelf = pathname.startsWith("/me/");
  const timeZone = useTimeZone();
  const today = todayKey(timeZone);

  // Visible month — starts at today's month in the viewer's effective zone.
  const [cursor, setCursor] = useState(() => ({
    year: Number(today.slice(0, 4)),
    month: Number(today.slice(5, 7)) - 1,
  }));
  const shiftMonth = (delta: number): void => {
    setCursor(({ year, month }) => {
      const d = new Date(Date.UTC(year, month + delta, 1));
      return { year: d.getUTCFullYear(), month: d.getUTCMonth() };
    });
  };

  const range = monthRange(cursor.year, cursor.month);
  const query = useLeaveWindow(range, isSelf);
  const holidays = useHolidays(range);

  const visible = useMemo(
    () => (query.data?.items ?? []).filter((r) => VISIBLE_STATUSES.has(r.status)),
    [query.data],
  );

  // One pass building a day → rows index for the viewed month (each row visits
  // only the days it covers inside the month).
  const rowsByDay = useMemo(() => {
    const map = new Map<DayKey, LeaveRequestRow[]>();
    for (const row of visible) {
      for (const day of dateDayKeys(row.start_date, row.end_date, range)) {
        const bucket = map.get(day);
        if (bucket) bucket.push(row);
        else map.set(day, [row]);
      }
    }
    return map;
    // range is rebuilt from the cursor each render; its parts keep the memo stable.
  }, [visible, range.from, range.to]);

  const holidayByDay = useMemo(
    () => new Map((holidays.data ?? []).map((h) => [h.date, h.name])),
    [holidays.data],
  );

  // Only meaningful while the viewed month contains today: the window holds
  // nothing outside it.
  const todayCount =
    today >= range.from && today <= range.to
      ? new Set((rowsByDay.get(today) ?? []).map((row) => row.employee_id)).size
      : 0;

  const monthLabel = new Intl.DateTimeFormat(i18n.language, {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(cursor.year, cursor.month, 1)));

  const chipTitle = (row: LeaveRequestRow): string =>
    t("leave.calendar.chip_title", {
      employee: displayName(row),
      range: formatDateRange(i18n.language, row.start_date, row.end_date),
      status: t(`leave.status.${row.status}`),
    });

  const renderDay = (day: DayKey): React.ReactNode => {
    const dayRows = rowsByDay.get(day) ?? [];
    return (
      <>
        {dayRows.slice(0, 3).map((row) => (
          // The chip names a PERSON, so it goes to that person. The leave
          // request has no address of its own today; when it gets one, this is
          // the cell that has to choose between the two.
          <PersonLink key={row.request_id} employeeId={row.employee_id}>
            <span
              title={chipTitle(row)}
              className={`block overflow-hidden text-ellipsis whitespace-nowrap rounded-full px-1.5 py-px font-sans text-[12px] font-semibold ${
                row.status === "approved"
                  ? "border border-[var(--accent)]/30 bg-[var(--accent-subtle-bg)] text-[var(--accent)]"
                  : "border border-dashed border-[var(--state-warning)] bg-[var(--bg-surface)] text-[var(--fg-muted)]"
              }`}
            >
              {displayName(row)}
            </span>
          </PersonLink>
        ))}
        {dayRows.length > 3 && (
          <span className="pl-1 font-sans text-[12px] text-[var(--fg-muted)]">
            {t("leave.calendar.more_count", { count: dayRows.length - 3 })}
          </span>
        )}
      </>
    );
  };

  return (
    <div className="mx-auto max-w-[1320px] space-y-6">
      <PageHeader
        title={isSelf ? t("leave.calendar.title_self") : t("leave.calendar.title")}
        description={isSelf ? t("leave.calendar.lead_self") : t("leave.calendar.lead")}
        actions={
          <>
            <Button variant="secondary" asChild>
              <RouterLink to={isSelf ? "/me/leave" : "/requests"}>
                <Rows3 size={14} aria-hidden="true" />
                {t("leave.calendar.list_view")}
              </RouterLink>
            </Button>
            <ScopeGate required={LEAVE_CAP_FILE} allowed={capabilities}>
              <Button variant="primary" asChild>
                <RouterLink to="/me/leave/file">
                  <Plus size={14} aria-hidden="true" />
                  {t("leave.calendar.request_leave")}
                </RouterLink>
              </Button>
            </ScopeGate>
          </>
        }
      />

      {/* Tur çapası ay gezinmesini DEĞİL bütün çubuğu çerçeveliyor: anlatılacak
          şeyin yarısı sağdaki lejant (onaylı dolu, bekleyen kesikli) ve o
          olmadan ızgaradaki renkler okunmuyor. */}
      <div data-tour="calendar-toolbar" className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-1">
          <IconButton
            variant="ghost"
            size="sm"
            aria-label={t("leave.calendar.prev_month")}
            onClick={(): void => {
              shiftMonth(-1);
            }}
          >
            <ChevronLeft size={15} aria-hidden="true" />
          </IconButton>
          <div
            aria-live="polite"
            className="min-w-[140px] text-center font-serif text-[15px] font-medium text-[var(--fg-primary)]"
          >
            {monthLabel}
          </div>
          <IconButton
            variant="ghost"
            size="sm"
            aria-label={t("leave.calendar.next_month")}
            onClick={(): void => {
              shiftMonth(1);
            }}
          >
            <ChevronRight size={15} aria-hidden="true" />
          </IconButton>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          <span className="flex items-center gap-1.5 font-sans text-[11.5px] text-[var(--fg-muted)]">
            <span
              aria-hidden="true"
              className="inline-block h-[11px] w-[11px] rounded-[3px] border border-[var(--accent)]/30 bg-[var(--accent-subtle-bg)]"
            />
            {t("leave.calendar.legend_approved")}
          </span>
          <span className="flex items-center gap-1.5 font-sans text-[11.5px] text-[var(--fg-muted)]">
            <span
              aria-hidden="true"
              className="inline-block h-[11px] w-[11px] rounded-[3px] border border-dashed border-[var(--state-warning)] bg-[var(--bg-surface)]"
            />
            {t("leave.calendar.legend_pending")}
          </span>
          <span className="flex items-center gap-1.5 font-sans text-[11.5px] text-[var(--fg-muted)]">
            <span
              aria-hidden="true"
              className="inline-block h-[11px] w-[11px] rounded-[3px] bg-[var(--state-danger)]/15"
            />
            {t("leave.calendar.legend_holiday")}
          </span>
          {todayCount > 0 && (
            <Badge tone="info">
              {t("leave.calendar.today_count", { count: todayCount })}
            </Badge>
          )}
        </div>
      </div>

      {query.data?.truncated === true && (
        <Alert severity="warning">{t("leave.calendar.truncated", { count: LEAVE_WINDOW_CAP })}</Alert>
      )}
      {holidays.isError && (
        <Alert severity="warning">{t("leave.calendar.holidays_error")}</Alert>
      )}

      {query.isPending ? (
        <Loading label={t("leave.calendar.loading")} rows={5} />
      ) : query.isError ? (
        <ErrorState
          title={t("leave.calendar.error_title")}
          description={errorMessage(query.error, t)}
        />
      ) : (
        <Card className="overflow-hidden" data-tour="leave-grid">
          <CalendarGrid
            variant="month"
            days={monthCells(cursor.year, cursor.month)}
            caption={t("leave.calendar.grid_caption")}
            locale={i18n.language}
            today={today}
            holidayOf={(day) => holidayByDay.get(day)}
            holidayLabel={t("leave.calendar.holiday_badge")}
            renderDay={renderDay}
          />
        </Card>
      )}
    </div>
  );
}
